<!-- Modal Header -->
<div class="modal-header">
  <h4 class="modal-title header-3">Test Coverage</h4>
  <button
    type="button"
    class="accello-icon-btn"
    (mousedown)="isActive = true"
    (mouseup)="isActive = false"
    (mouseleave)="isActive = false"
    (click)="activeModal.dismiss('Cross click')"
  >
    <img
      [src]="isActive
        ? 'assets/images/virtusa-icons/close-dark.svg'
        : 'assets/images/virtusa-icons/close.svg'"
      alt="close"
    />
  </button>
</div>

<!-- Modal Body -->
<div class="modal-body tc-body">

  <!-- Loading State -->
  <ng-container *ngIf="isLoading">
    <div class="tc-loading">
      <div class="tc-spinner"></div>
      <div class="tc-progress-container">
        <div class="tc-progress-header">
          <span class="tc-loading-text">{{ progressMessage || 'Analyzing test coverage...' }}</span>
          <span class="tc-progress-pct">{{ progressPercentage }}%</span>
        </div>
        <div class="tc-progress-track">
          <div class="tc-progress-fill" [style.width.%]="progressPercentage"></div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Error State -->
  <ng-container *ngIf="!isLoading && errorMessage">
    <div class="tc-error">
      <span class="tc-error-text">{{ errorMessage }}</span>
    </div>
  </ng-container>

  <!-- Loaded State -->
  <ng-container *ngIf="!isLoading && !errorMessage && coverageData">

    <!-- Overall Error Warning -->
    <div *ngIf="overallError" class="tc-overall-warning">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 1.5L14.928 13.5H1.072L8 1.5Z" stroke="#E07B00" stroke-width="1.4" fill="none"/>
        <text x="8" y="12" text-anchor="middle" font-size="7" fill="#E07B00" font-weight="700">!</text>
      </svg>
      <span class="tc-overall-warning-text">{{ overallError }}</span>
    </div>

    <!-- Summary Cards -->
    <div class="tc-summary-row">
      <div class="tc-summary-card">
        <span class="tc-summary-label">Total Stories Analyzed</span>
        <span class="tc-summary-value">{{ formatCount(coverageData.totalStoriesAnalyzed) }}</span>
      </div>
      <div class="tc-summary-card">
        <span class="tc-summary-label">Avg. Test Coverage</span>
        <span class="tc-summary-value tc-val-purple">{{ coverageData.avgTestCoverage }}%</span>
      </div>
      <div class="tc-summary-card">
        <span class="tc-summary-label">Avg. Test Accuracy</span>
        <span class="tc-summary-value tc-val-purple">{{ coverageData.avgTestAccuracy }}%</span>
      </div>
      <div class="tc-summary-card">
        <span class="tc-summary-label">Total Uncovered Criteria</span>
        <span class="tc-summary-value tc-val-red">{{ formatCount(coverageData.totalUncoveredCriteria) }}</span>
      </div>
    </div>

    <!-- Breakdown Section -->
    <div class="tc-breakdown">
      <span class="tc-breakdown-title">
        User Story Breakdown ({{ coverageData.stories.length }})
      </span>

      <div class="tc-story-list">
        <ng-container *ngFor="let story of coverageData.stories">
          <!-- Story Row -->
          <div class="tc-story-row">

            <!-- Info: ID, badges, title -->
            <div class="tc-story-info">
              <div class="tc-story-meta">
                <span class="tc-story-id">{{ story.id }}</span>
                <span class="tc-badge" [ngClass]="getCoverageClass(story.coverageStatus)">
                  {{ getCoverageLabel(story.coverageStatus) }}
                </span>
                <span *ngIf="story.uncoveredCriteria.length" class="tc-badge tc-badge--uncovered">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 1L11.196 10H0.804L6 1Z" stroke="#E07B00" stroke-width="1.2" fill="none"/>
                    <text x="6" y="9" text-anchor="middle" font-size="5.5" fill="#E07B00" font-weight="700">!</text>
                  </svg>
                  {{ formatCount(story.uncoveredCriteria.length) }} Uncovered Criteria Found
                </span>
              </div>
              <p class="tc-story-title header-8">{{ story.title }}</p>
            </div>

            <!-- Test Cases -->
            <div class="tc-test-cases">
              <img src="assets/images/virtusa-icons/testcase.svg" alt="Test case icon" />
              <span class="test-case-text">{{ story.testCasesGenerated }} Test Cases Generated</span>
            </div>

            <!-- Accuracy & Coverage bars -->
            <div class="tc-metrics">
              <div class="tc-metric">
                <div class="tc-metric-label-wrapper">
                  <span class="tc-metric-label">Accuracy</span>
                  <span class="tc-info-icon-wrapper">
                    <img 
                      src="assets/images/virtusa-icons/info-gray.png" 
                      alt="Info" 
                      class="tc-info-icon"
                    />
                    <span class="tc-custom-tooltip">
                      Measures how well the generated test cases match the expected test scenarios in terms of alignment and quality.
                      <br><br>
                      Accuracy = (Alignment Score × 40%) + (Test Quality Score × 60%)
                    </span>
                  </span>
                </div>
                <div class="tc-bar-row">
                  <div class="tc-bar-track">
                    <div class="tc-bar-fill tc-bar--accuracy" [style.width.%]="story.accuracy"></div>
                  </div>
                  <span class="tc-bar-pct">{{ story.accuracy }}%</span>
                </div>
              </div>
              <div class="tc-metric">
                <div class="tc-metric-label-wrapper">
                  <span class="tc-metric-label">Coverage</span>
                  <span class="tc-info-icon-wrapper">
                    <img 
                      src="assets/images/virtusa-icons/info-gray.png" 
                      alt="Info" 
                      class="tc-info-icon"
                    />
                    <span class="tc-custom-tooltip">
                      Measures how much of the business requirements are covered by the generated test cases.
                      <br><br>
                      Coverage (%) = (Covered Requirements ÷ Total Business Requirements) × 100
                    </span>
                  </span>
                </div>
                <div class="tc-bar-row">
                  <div class="tc-bar-track">
                    <div
                      class="tc-bar-fill"
                      [ngClass]="getCoverageBarClass(story.coverage)"
                      [style.width.%]="story.coverage"
                    ></div>
                  </div>
                  <span class="tc-bar-pct general-text-9">{{ story.coverage }}%</span>
                </div>
              </div>
            </div>

            <!-- Expand/Collapse button -->
            <button
              *ngIf="hasExpandableContent(story)"
              class="tc-expand-btn"
              [attr.aria-label]="isExpanded(story.id) ? 'Collapse' : 'Expand'"
              (click)="toggleStory(story.id)"
            >
              <img *ngIf="!isExpanded(story.id)" src="assets/images/virtusa-icons/add_icon.svg" alt="Expand icon" />
              <img *ngIf="isExpanded(story.id)" src="assets/images/virtusa-icons/remove_icon.svg" alt="Collapse icon" />
            </button>
            <div *ngIf="!hasExpandableContent(story)" class="tc-expand-placeholder"></div>
          </div>

          <!-- Expanded Panel -->
          <div *ngIf="isExpanded(story.id)" class="tc-uncovered-panel">

            <!-- Warnings -->
            <ng-container *ngIf="story.warnings.length">
              <p class="tc-uncovered-title">Warnings ({{ formatCount(story.warnings.length) }})</p>
              <div class="tc-criteria-list tc-warnings-list">
                <div *ngFor="let warning of story.warnings" class="tc-criteria-item tc-warning-item">
                  <span class="tc-criteria-dot tc-warning-dot"></span>
                  <span class="criteria-text tc-warning-text">{{ warning }}</span>
                </div>
              </div>
            </ng-container>

            <!-- Uncovered Criteria -->
            <ng-container *ngIf="story.uncoveredCriteria.length">
              <p class="tc-uncovered-title">Uncovered Criteria ({{ formatCount(story.uncoveredCriteria.length) }})</p>
              <div class="tc-criteria-list">
                <div *ngFor="let criterion of story.uncoveredCriteria" class="tc-criteria-item">
                  <span class="tc-criteria-dot"></span>
                  <span class="criteria-text">{{ criterion }}</span>
                </div>
              </div>
            </ng-container>

            <!-- Analysis Summary -->
            <ng-container *ngIf="story.analysisSummary">
              <p class="tc-uncovered-title">Analysis Summary</p>
              <p class="tc-analysis-summary">{{ story.analysisSummary }}</p>
            </ng-container>

          </div>

          <div class="tc-divider"></div>
        </ng-container>
      </div>
    </div>

  </ng-container>

</div>

<!-- Modal Footer -->
<div class="modal-footer justify-content-end">
  <button type="button" class="btn-cloase" (click)="activeModal.close()">
    Close
  </button>
</div>
